import React, { useCallback, useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import debounce from 'lodash.debounce'
import styled from 'styled-components'

import { getConfiguration } from 'utils/editorConfigurations'
import { useDidUpdate } from 'utils/hooks'

import CenteredLoader from '../lib/CenteredLoader'

const SYNC_DEBOUNCE_TIME = 1000

/**
 * CKEditor (source mode) is changing HTML as it wants
 * if @allowedContent option not true),
 * it makes some attributes swapping and removing.
 * So inserted HTML may be changed by it.
 */
const CKEditor = ({
  id,
  value,
  onChange,
  configuration,
  placeholdersFields,
  onSourceModeToggle,
  disabled,
  className,
  /** true for cases when outside updates of value are possible after initialize  */
  activeUpdateMode,
  debounceTime,
  onBlur,
}) => {
  /**
   * CKEditor instance.setData is async function,
   * so we have to handle the state of it.
   */
  const elementName = `editor_${id}`
  const editorRef = useRef(null)
  const configRef = useRef(getConfiguration(configuration, placeholdersFields))

  const [syncInProgress, setSyncInProgress] = useState(false)

  const handleSourceModeToggle = useCallback(
    (e) => {
      if (typeof onSourceModeToggle === 'function') {
        onSourceModeToggle(e.sender.mode === 'source')
      }
    },
    [onSourceModeToggle]
  )

  const handleOnChange = useCallback(
    debounce(() => {
      setSyncInProgress(true)
      onChange(editorRef.current.getData())
      setSyncInProgress(false)
    }, debounceTime || SYNC_DEBOUNCE_TIME),
    [onChange]
  )

  const handleOnBlur = () => {
    onBlur?.(editorRef.current.getData())
  }

  useEffect(
    () => () => {
      handleOnChange.cancel()
    },
    []
  )

  useDidUpdate(() => {
    if (
      activeUpdateMode &&
      !syncInProgress &&
      editorRef.current &&
      editorRef.current.getData() !== value
    ) {
      editorRef.current.setData(value)
    }
  }, [value])

  const handleInstanceReady = (e) => {
    const container = document.querySelector(`.${elementName}-container`)
    if (container) {
      container.classList.add('instanceIsReady')
    }

    e.editor.dataProcessor.writer.indentationChars = '  '
    e.editor.dataProcessor.htmlFilter.addRules({
      elements: {
        $(element) {
          // Output dimensions of images as width and height
          if (element.name === 'img') {
            const style = element.attributes.style

            if (style) {
              // Get the width from the style.
              let match = /(?:^|\s)width\s*:\s*(\d+)px/i.exec(style)
              const width = match && match[1]

              // Get the height from the style.
              match = /(?:^|\s)height\s*:\s*(\d+)px/i.exec(style)
              const height = match && match[1]

              // Get the float from the style.
              match = /(?:^|\s)float\s*:\s*(\w+)/i.exec(style)
              const float = match && match[1]

              if (width) {
                element.attributes.style = element.attributes.style.replace(
                  /(?:^|\s)width\s*:\s*(\d+)px;?/i,
                  ''
                )
                element.attributes.width = width
              }

              if (height) {
                element.attributes.style = element.attributes.style.replace(
                  /(?:^|\s)height\s*:\s*(\d+)px;?/i,
                  ''
                )
                element.attributes.height = height
              }
              if (float) {
                element.attributes.style = element.attributes.style.replace(
                  /(?:^|\s)float\s*:\s*(\w+)/i,
                  ''
                )
                element.attributes.align = float
              }
            }
          }
          if (!element.attributes.style) delete element.attributes.style
          return element
        },
      },
    })
  }

  useEffect(() => {
    if (!window.CKEDITOR.instances[elementName]) {
      editorRef.current = window.CKEDITOR.replace(elementName, configRef.current)
    }
    return () => {
      if (document.body.clientWidth === 0) {
        document.body.parentElement.removeAttribute('style')
        document.body.removeAttribute('style')
      }
      if (editorRef.current) {
        editorRef.current.removeAllListeners()
        window.CKEDITOR.remove(editorRef.current)
      }
    }
  }, [elementName])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.on('instanceReady', handleInstanceReady)
    }
  }, [handleInstanceReady])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.on('mode', handleSourceModeToggle)
    }
  }, [handleSourceModeToggle])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.on('change', handleOnChange)
    }
  }, [handleOnChange])

  useEffect(() => {
    if (editorRef.current && onBlur) {
      editorRef.current.on('blur', handleOnBlur)
    }
  }, [handleOnBlur])

  return (
    <div
      className={cls(className, `${elementName}-container`, {
        disabled,
      })}
    >
      <CenteredLoader overlap style={{ zIndex: 1 }} />

      <textarea id={elementName} name={elementName} value={value} />
    </div>
  )
}

export default styled(CKEditor)`
  height: 142px;
  position: relative;
  overflow: hidden;

  &.instanceIsReady {
    height: auto;
    overflow: auto;

    .loader-overlap-container {
      display: none;
    }
  }

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }
`
